"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { DevaKathaLS2 } from "./DevaKathaLS2";
import { DevaKathaLS3 } from "./DevaKathaLS3";
import { DevaKathaLS5 } from "./DevaKathaLS5";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Kedarnath "Sthapna aur Panch Kedar" (prod 1f1e80a8) — Trimbakeshwar holes treatment.
// Top-left holes-masked phrase highlights, per-word colour, word-by-word drop-in.
// Every `start` = EXACT word_timestamps_hindi time of the phrase's key word. 1280×704, fps from comp.
const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";
const HOLES = HOLES_DATA_URI;

const BASE_FS = 66, BIG_FS = 92, SMALL_FS = 46;
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -10;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

// 37 phrase cards. Numerals stay Latin (12,000 / 400 / 32 / 16 / 2013 / 1,000); month जून Devanagari.
export const KEDAR_PK_PHRASES: Phrase[] = [
  // ---- HOOK ----
  { start: 0.4,    out: 4.0,    lines: [[{ t: "12,000", c: "w", big: true }, { t: "फुट", c: "y" }]] },
  { start: 19.98,  out: 22.6,   lines: [[{ t: "त्रिकोणीय", c: "y" }], [{ t: "चट्टान", c: "w", big: true }]] },
  { start: 22.81,  out: 26.0,   lines: [[{ t: "बैल", c: "y" }, { t: "का", small: true }, { t: "कूबड़", c: "w", big: true }]] },
  { start: 53.52,  out: 56.9,   lines: [[{ t: "पंच", c: "y" }, { t: "केदार", c: "w", big: true }]] },
  { start: 58.15,  out: 61.5,   lines: [[{ t: "अनसुलझा", c: "r" }], [{ t: "रहस्य", c: "w", big: true }]] },
  { start: 74.56,  out: 77.9,   lines: [[{ t: "400", c: "w", big: true }, { t: "साल", c: "w", big: true }], [{ t: "बर्फ़", c: "y" }, { t: "में", small: true }, { t: "दबा", c: "y" }]] },
  { start: 94.26,  out: 97.6,   lines: [[{ t: "भविष्यवाणी", c: "y", big: true }]] },
  // ---- MAHABHARAT / PANCH KEDAR STORY ----
  { start: 125.52, out: 128.9,  lines: [[{ t: "महाभारत", c: "w", big: true }]] },
  { start: 143.36, out: 146.7,  lines: [[{ t: "ब्रह्म-हत्या", c: "r", big: true }]] },
  { start: 178.0,  out: 181.4,  lines: [[{ t: "गुप्तकाशी", c: "y", big: true }]] },
  { start: 186.27, out: 189.6,  lines: [[{ t: "बैल", c: "y" }, { t: "का", small: true }, { t: "रूप", c: "w", big: true }]] },
  { start: 228.18, out: 231.5,  lines: [[{ t: "पाँच", c: "w", big: true }, { t: "टुकड़े", c: "y" }]] },
  { start: 293.24, out: 296.6,  lines: [[{ t: "तुंगनाथ", c: "y" }], [{ t: "सबसे", c: "w" }, { t: "ऊँचा", c: "w" }, { t: "शिवधाम", small: true }]] },
  { start: 388.52, out: 391.9,  lines: [[{ t: "डोलेश्वर", c: "y" }], [{ t: "महादेव", c: "w", big: true }]] },
  { start: 437.51, out: 441.5,  lineGap: 6, lines: [[{ t: "भारत", c: "w" }, { t: "=", small: true }, { t: "धड़", c: "y" }], [{ t: "नेपाल", c: "w" }, { t: "=", small: true }, { t: "मस्तक", c: "y" }]] },
  // ---- NAR-NARAYAN / STHAPNA ----
  { start: 458.3,  out: 461.7,  lines: [[{ t: "नर", c: "w", big: true }, { t: "नारायण", c: "y" }]] },
  { start: 491.12, out: 493.9,  lines: [[{ t: "तथास्तु", c: "y", big: true }]] },
  { start: 494.26, out: 497.6,  lines: [[{ t: "कॉस्मिक", c: "y" }], [{ t: "कॉन्ट्रैक्ट", c: "w", big: true }]] },
  // ---- ARCHITECTURE ----
  { start: 580.34, out: 583.7,  lines: [[{ t: "IIT", c: "w", big: true }, { t: "मद्रास", c: "y" }]] },
  { start: 588.66, out: 592.0,  lines: [[{ t: "बिना", c: "w" }, { t: "सीमेंट", c: "y", big: true }]] },
  { start: 610.34, out: 613.7,  lines: [[{ t: "लिटिल", c: "w" }, { t: "आइस", c: "y" }, { t: "एज", c: "y" }]] },
  // ---- ADI SHANKARACHARYA ----
  { start: 649.57, out: 652.9,  lh: 1.28, lines: [[{ t: "आदि", c: "w" }, { t: "शंकराचार्य", c: "y" }]] },
  { start: 665.69, out: 669.0,  lines: [[{ t: "चार", c: "w", big: true }, { t: "धाम", c: "y" }]] },
  { start: 701.27, out: 704.6,  lines: [[{ t: "32", c: "w", big: true }, { t: "साल", c: "y" }], [{ t: "में", small: true }, { t: "देह", c: "y" }, { t: "त्याग", c: "y" }]] },
  // ---- RAWAL / WEALTH / DONORS ----
  { start: 733.1,  out: 736.4,  lines: [[{ t: "रावल", c: "y", big: true }]] },
  { start: 868.37, out: 871.7,  lines: [[{ t: "काली", c: "y" }, { t: "कमली", c: "w", big: true }]] },
  // ---- 6-MONTH CLOSING / BHAIRAVNATH ----
  { start: 926.52, out: 929.9,  lines: [[{ t: "छह", c: "w", big: true }, { t: "महीने", c: "y" }], [{ t: "बर्फ़", c: "y" }, { t: "में", small: true }]] },
  { start: 959.24, out: 962.6,  lines: [[{ t: "अखंड", c: "y" }], [{ t: "ज्योति", c: "w", big: true }]] },
  { start: 973.18, out: 976.5,  lines: [[{ t: "भैरवनाथ", c: "y", big: true }]] },
  // ---- 2013 DISASTER ----
  { start: 1033.64, out: 1037.0, lines: [[{ t: "16", c: "w", big: true }, { t: "जून", c: "y" }, { t: "2013", c: "w", big: true }]] },
  { start: 1042.36, out: 1045.7, lines: [[{ t: "चोराबाड़ी", c: "y" }], [{ t: "झील", c: "w", big: true }]] },
  { start: 1065.15, out: 1068.5, lines: [[{ t: "डेब्रिस", c: "r" }, { t: "फ्लो", c: "r", big: true }]] },
  { start: 1086.81, out: 1090.2, lines: [[{ t: "1,000", c: "w", big: true }, { t: "टन", c: "y" }]] },
  { start: 1098.75, out: 1102.1, lines: [[{ t: "भीम", c: "y" }, { t: "शिला", c: "w", big: true }]] },
  { start: 1110.71, out: 1114.0, lines: [[{ t: "जियोलॉजिकल", c: "y" }], [{ t: "अनॉमली", c: "w", big: true }]] },
  { start: 1118.18, out: 1121.5, lines: [[{ t: "तथास्तु", c: "y", big: true }]] },
  // ---- PROPHECY / FUTURE ----
  { start: 1172.18, out: 1175.5, lines: [[{ t: "भविष्य", c: "y" }, { t: "केदार", c: "w", big: true }]] },
];

const PhraseBlock: React.FC<{ p: Phrase; fps: number }> = ({ p, fps }) => {
  const frame = useCurrentFrame();
  const t = frame / fps;
  const phraseOp = interpolate(t, [p.out - 0.4, p.out], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (t < p.start - 0.1 || t > p.out + 0.1) return null;
  const gap = p.lineGap ?? LINE_GAP;
  const lh = p.lh ?? LH;
  let gi = -1;
  return (
    <div style={{ position: "absolute", top: PAD, left: PAD, fontFamily, opacity: phraseOp, display: "flex", flexDirection: "column" }}>
      {p.lines.map((line, li) => (
        <div key={li} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "0 16px", marginTop: li === 0 ? 0 : gap }}>
          {line.map((w, wi) => {
            gi += 1;
            const aT = p.start + gi * STAGGER;
            const k = Math.min(Math.max((t - aT) / DUR, 0), 1);
            const ease = 1 - Math.pow(1 - k, 3);
            const op = interpolate(t, [aT, aT + 0.18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const y = -DROP * (1 - ease);
            return (
              <span key={wi} style={{
                display: "inline-block",
                transform: `translateY(${y}px)`,
                opacity: op,
                color: col(w.c),
                fontSize: w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS,
                fontWeight: w.big ? 900 : 800,
                lineHeight: lh,
                letterSpacing: p.ls ?? "0.005em",
                textShadow: SH,
              }}>{w.t}</span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Bottom-RIGHT viewing tip (Kamakhya style) — 2 lines, right-aligned, NO holes mask.
const WatchTag: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.4, out - 0.4, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, right: PAD, fontFamily, opacity: op, textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
      <span style={{ color: YELLOW, fontSize: SMALL_FS, fontWeight: 800, letterSpacing: "0.01em", textShadow: SH }}>Watch at 1.2x</span>
      <span style={{ color: WHITE, fontSize: SMALL_FS * 0.7, fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>for best experience</span>
    </div>
  );
};

// Bottom-LEFT "आगे देखिए" teaser (Kamakhya style) — yellow, WHITE cycling dots, SMALL_FS, NO holes.
const AageDekhiye: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.4, out - 0.5, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const dots = [".", "..", "..."][Math.floor((t * 1.5) % 4)] ?? "";
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, left: PAD, fontFamily, opacity: op }}>
      <span style={{ color: YELLOW, fontSize: SMALL_FS, fontWeight: 800, letterSpacing: "0.01em", textShadow: SH }}>आगे देखिए</span>
      <span style={{ color: WHITE, fontSize: SMALL_FS, fontWeight: 800, letterSpacing: "0.05em", textShadow: SH }}>{dots}</span>
    </div>
  );
};

// Center "हर हर महादेव" chant card — gold, gentle fade + pulse. NO holes.
const HarHarCard: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.45, out - 0.5, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const sc = interpolate(t, [start, start + 0.6], [0.9, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const pulse = 1 + 0.02 * Math.sin((t - start) * 3.2);
  return (
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center" }}>
      <span style={{
        fontFamily, color: YELLOW, fontSize: 84, fontWeight: 900, letterSpacing: "0.01em",
        textShadow: "0 6px 18px rgba(0,0,0,0.92), 0 2px 6px rgba(0,0,0,0.95)",
        opacity: op, transform: `scale(${sc * pulse})`,
      }}>“हर हर महादेव”</span>
    </AbsoluteFill>
  );
};

// Master comp — holes-masked devotional phrases + non-masked UI (teaser, watch tip, har-har, LS pills).
export const KedarnathPanchKedarOverlaysFull: React.FC<{ phrases?: Phrase[] }> = ({ phrases = KEDAR_PK_PHRASES }) => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      {/* holes distress mask over the dramatic top-left phrases only */}
      <AbsoluteFill style={{
        backgroundColor: "transparent",
        WebkitMaskImage: `url(${HOLES})`, maskImage: `url(${HOLES})`,
        WebkitMaskSize: "1280px 704px", maskSize: "1280px 704px",
        WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
        WebkitMaskPosition: "0 0", maskPosition: "0 0",
      }}>
        {phrases.map((p, i) => <PhraseBlock key={i} p={p} fps={fps} />)}
      </AbsoluteFill>

      {/* आगे देखिए teaser (bottom-left) over the opening hook */}
      <AageDekhiye fps={fps} start={3.0} out={92.0} />
      {/* viewing tip (bottom-right, 2 lines) — opening */}
      <WatchTag fps={fps} start={1.0} out={6.5} />

      {/* "हर हर महादेव" center card @ the spoken chants (hook 122.19s + outro 1223.24s) */}
      <HarHarCard fps={fps} start={122.0} out={125.5} />
      <HarHarCard fps={fps} start={1221.0} out={1224.15} />

      {/* Like+Subscribe — LS2 (bell) @ first "सब्सक्राइब" 115.77s · LS3 @ mid "सब्सक्राइब" 1018.83s · LS5 @ outro "सब्सक्राइब" 1214.01s */}
      <Sequence from={S(115.5)} durationInFrames={S(5.2)}><DevaKathaLS2 /></Sequence>
      <Sequence from={S(1018.6)} durationInFrames={S(5.2)}><DevaKathaLS3 /></Sequence>
      <Sequence from={S(1213.8)} durationInFrames={S(5.2)}><DevaKathaLS5 /></Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
